import React, { useLayoutEffect, useRef } from 'react';
import { suppressPasswordManagersInOperationalTree } from '../../utils/no-password-manager';

interface OperationalFieldBoundaryProps {
    children: React.ReactNode;
}

/**
 * Password managers belong on QuoteMaker's authentication surfaces, not on
 * customer, address, pricing, scope or project-authoring fields. This boundary
 * is mounted only after authentication and marks current and future business
 * inputs with the shared opt-out contract.
 */
export default function OperationalFieldBoundary({ children }: OperationalFieldBoundaryProps) {
    const rootRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        suppressPasswordManagersInOperationalTree(root);

        const observer = typeof MutationObserver === 'undefined'
            ? null
            : new MutationObserver((records) => {
                for (const record of records) {
                    for (const node of Array.from(record.addedNodes)) {
                        if (node instanceof Element) {
                            suppressPasswordManagersInOperationalTree(node);
                        }
                    }
                }
            });
        observer?.observe(root, { childList: true, subtree: true });

        // Dynamic editors can reuse an existing node and change its field
        // semantics. Reassert the contract before the operator types.
        const handleFocusIn = (event: FocusEvent) => {
            if (event.target instanceof Element) {
                suppressPasswordManagersInOperationalTree(event.target);
            }
        };
        root.addEventListener('focusin', handleFocusIn, true);

        return () => {
            observer?.disconnect();
            root.removeEventListener('focusin', handleFocusIn, true);
        };
    }, []);

    return (
        <div
            ref={rootRef}
            data-qm-operational-field-boundary="true"
            style={{ display: 'contents' }}
        >
            {children}
        </div>
    );
}
